import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Receipt, ShieldCheck, Send } from "lucide-react";
import { useLang, CONTACT } from "../../translations";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const f = t.contact.form;

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-cream border border-ink/15 rounded-xl px-4 py-3.5 text-ink placeholder-muted2/60 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-sand">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{t.contact.label}</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-[3.4rem] text-ink leading-[1.05] tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-lg text-muted2 leading-relaxed max-w-md">{t.contact.intro}</p>

          <div className="mt-8 space-y-4">
            <a data-testid="contact-phone-link" href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 text-ink hover:text-sage transition-colors duration-300">
              <span className="h-11 w-11 rounded-full bg-sage/12 flex items-center justify-center text-sage"><Phone size={18} /></span>
              {CONTACT.phone}
            </a>
            <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-ink hover:text-sage transition-colors duration-300">
              <span className="h-11 w-11 rounded-full bg-sage/12 flex items-center justify-center text-sage"><Mail size={18} /></span>
              {CONTACT.email}
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-ink/10 bg-cream p-5">
              <Receipt size={18} className="text-sage" />
              <h4 className="font-heading text-xl text-ink mt-3">{t.contact.fee}</h4>
              <p className="text-sm text-muted2 mt-2 leading-relaxed">{t.contact.feeText}</p>
            </div>
            <div className="rounded-xl border border-ink/10 bg-cream p-5">
              <ShieldCheck size={18} className="text-sage" />
              <h4 className="font-heading text-xl text-ink mt-3">{t.contact.insurance}</h4>
              <p className="text-sm text-muted2 mt-2 leading-relaxed">{t.contact.insuranceText}</p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="rounded-[1.5rem] border border-ink/10 bg-white p-6 md:p-9">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="name" label={f.name} value={form.name} onChange={set("name")} required cls={inputCls} />
              <Field id="email" type="email" label={f.email} value={form.email} onChange={set("email")} required cls={inputCls} />
              <Field id="phone" label={f.phone} value={form.phone} onChange={set("phone")} cls={inputCls} />
              <Field id="subject" label={f.subject} value={form.subject} onChange={set("subject")} cls={inputCls} />
            </div>
            <div className="relative mt-4">
              <textarea
                data-testid="contact-message"
                id="message"
                required
                rows={5}
                placeholder={f.message}
                aria-label={f.message}
                value={form.message}
                onChange={set("message")}
                className={inputCls + " resize-none"}
              />
            </div>
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-cream px-8 py-4 hover:bg-sage transition-colors duration-300 disabled:opacity-60"
            >
              <Send size={17} /> {loading ? f.sending : f.send}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

const Field = ({ id, label, value, onChange, type = "text", required, cls }) => (
  <div className="relative">
    <input
      data-testid={`contact-${id}`}
      id={id}
      type={type}
      required={required}
      placeholder={label}
      aria-label={label}
      value={value}
      onChange={onChange}
      className={cls}
    />
  </div>
);
